import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-tailwindcss-select";
import { getCities } from "../../Services/citiesApi";
import { role } from "../../Constants/role";
const UserPermissionModal = ({ user, onClose, onSave, isSuperAdmin }) => {
  const { t } = useTranslation();
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null); // For Super City Admin
  const [selectedCities, setSelectedCities] = useState([]); // For City Admin
  const [citiesList, setCitiesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCities();
    // Set initial role and cities if user has them
    if (!isSuperAdmin) {
      setSelectedRole(role.TerminalAdmin);
    }

    // // If Super City Admin, set single city
    // if (user?.roleId === role.SuperAdmin && user?.city) {
    //   setSelectedCity({
    //     value: user.city.id,
    //     label: user.city.name,
    //   });
    // }

    // // If City Admin, set multiple cities
    // if (user?.roleId === role.TerminalAdmin && user?.cities) {
    //   const userCities = user.cities.map((city) => ({
    //     value: city.id,
    //     label: city.name,
    //   }));
    //   setSelectedCities(userCities);
    // }
  }, [user]);

  const fetchCities = async () => {
    try {
      const citiesResponse = await getCities();
      setCitiesList(
        citiesResponse.data.data?.map((city) => ({
          value: city.id,
          label: city.name,
        }))
      );
    } catch (e) {
      console.error("Error fetching cities:", e);
      setError("Failed to load cities");
    }
  };

  const handleCityChange = (value) => {
    setSelectedCity(value);
  };

  const handleCitiesChange = (value) => {
    setSelectedCities(value);
  };

  // Filter out already selected cities from the options
  const availableCitiesOptions = useMemo(() => {
    if (!citiesList || citiesList.length === 0) return [];
    
    // Get IDs of already selected cities
    const selectedCityIds = selectedCities.map((city) => city.value);
    
    // Filter out already selected cities
    return citiesList.filter(
      (city) => !selectedCityIds.includes(city.value)
    );
  }, [citiesList, selectedCities]);

  const handleRoleChange = (option) => {
    setSelectedRole(Number(option.value));

    // Clear city selections when role changes
    setSelectedCity(null);
    setSelectedCities([]);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      let permissionData = {
        roleId: selectedRole,
      };
         
      // If Super City Admin, send single cityId
      if (selectedRole === role.superCityAdmin &&isSuperAdmin) {
        permissionData.cityId = selectedCity?.value;
      }
      // If City Admin, send multiple citiesIds
      else if (selectedRole === role.TerminalAdmin&&isSuperAdmin) {
        permissionData.cityId = selectedCities.map((c) => c.value);
      }
      // If Citizen, no city data needed

      await onSave(permissionData);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update permissions");
    } finally {
      setLoading(false);
      
    }
  };

  // Role options based on user type
  const roleOptions = isSuperAdmin
    ? [
        { value: role.TerminalAdmin, label: t("cityAdmins") || "City Admin" },
        {
          value: role.superCityAdmin,
          label: t("superCityAdmins") || "Super City Admin",
        },
      ]
    : [
        { value: role.TerminalAdmin, label: t("cityAdmins") || "City Admin" },
        // { value: role.contentCreator, label: t("citizen") },
      ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full relative max-h-[90vh] overflow-y-auto" 
       onMouseDown={(e) => {
        e.stopPropagation(); // ✅ THIS IS THE KEY
        }}
      >
        <button
          className="absolute top-2 right-3 text-gray-500 hover:text-black text-2xl"
          onMouseDown={(e) => {
            e.preventDefault(); // ✅ KEY
            e.stopPropagation(); // ✅ KEY
            onClose(); // close modal immediately
          }}
        >
          &times;
        </button>

        <h2 className="text-2xl font-bold mb-4 text-center">
          {t("manage_permissions") || "Manage User Permissions"}
        </h2>

        <div className="mb-4 p-3 bg-gray-100 rounded-lg">
          <p className="text-sm text-gray-600">
            <strong>{t("user") || "User"}:</strong> {user?.name}{" "}
          </p>
          <p className="text-sm text-gray-600">
            <strong>{t("email") || "Email"}:</strong> {user?.email}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block font-medium mb-2">
              {t("role") || "Role"} <span className="text-red-500">*</span>
            </label>
            <Select
              primaryColor="indigo"
              value={
                roleOptions.find((opt) => Number(opt.value) === selectedRole) ||
                null
              }
              onChange={handleRoleChange}
              options={roleOptions}
              classNames={{
                menu: "max-h-40 overflow-y-auto",
                list: "space-y-1",
                option: "py-1 px-2",
              }}
            />
            <p className="text-xs text-gray-500 mt-1">
              {isSuperAdmin
                ? t("change_role_user_superadmin")
                : t("change_role_user_superCityAdmin")}
            </p>
          </div>

          {selectedRole === role.TerminalAdmin && isSuperAdmin && (
            <div>
              <label className="block font-medium mb-2">
                {t("selectCity") || "Select Cities"}{" "}
                <span className="text-red-500">*</span>
              </label>
              <Select
                primaryColor="indigo"
                value={selectedCities}
                isMultiple
                onChange={handleCitiesChange}
                options={availableCitiesOptions}
                placeholder={
                  availableCitiesOptions.length === 0
                    ? t("all_cities_selected") || "All cities have been selected"
                    : t("select_assign_city") || "Select cities to assign"
                }
                classNames={{
                  menu: "max-h-40 overflow-y-auto",
                  list: "space-y-1",
                  option: "py-1 px-2",
                }}
                noOptionsMessage={availableCitiesOptions?.length === 0 ? t("noDataFound"): null}
              />
              <p className="text-xs text-gray-500 mt-1">
                 { t("city_admin_manage_multi_city") ||
                  "City Admins can manage multiple cities"
                }
              </p>
            </div>
          )}

          {selectedRole === role.superCityAdmin && isSuperAdmin && (
            <div>
              <label className="block font-medium mb-2">
                {t("selectCity") || "Select City"}{" "}
                <span className="text-red-500">*</span>
              </label>
              <Select
                primaryColor="indigo"
                value={selectedCity}
                onChange={handleCityChange}
                options={citiesList}
                placeholder={
                  t("select_assign_city") || "Select cities to assign"
                }
                classNames={{
                  menu: "max-h-40 overflow-y-auto",
                  list: "space-y-1",
                  option: "py-1 px-2",
                }}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t("super_city_admin") || "Super City Admins manage one city"}
              </p>
            </div>
          )}

          {error && <p className="text-sm text-red-500 text-center">{error}</p>}

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
             onMouseDown={(e) => {
            e.preventDefault(); // ✅ KEY
            e.stopPropagation(); // ✅ KEY
            onClose(); // close modal immediately
          }}
              disabled={loading}
            >
              {t("cancel") || "Cancel"}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={
                loading ||
                !selectedRole ||
                (selectedRole === role.superCityAdmin && !selectedCity) ||
                (selectedRole === role.TerminalAdmin &&
                 (isSuperAdmin&& selectedCities.length === 0))
              }
            >
              {loading
                ? t("saving") || "Saving..."
                : t("save_permissions") || "Save Permissions"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserPermissionModal;